import type { Column } from '@rundown-studio/types'

import { userStore } from '@/features/user/user.store'
import { gridState } from '../grid/grid.store'

import { useRundownAccess } from '@/features/permissions/lib/use-rundown-access'
import { RundownPermission } from '@rundown-studio/types'

/**
 * Determines if a column is a private notes column accessible to a specific user.
 *
 * @param {Pick<Column, 'name' | 'privateUid'>} column - The column to check, containing name and `privateUid` properties
 * @param {string} userId - User ID to check against the column's `privateUid`
 * @returns {boolean} - `true` if the column is a private notes column and belongs to the specified user
 */
export function getIsPrivateNotesColumn(
  column: Pick<Column, 'name' | 'privateUid'>,
): boolean {
  return (
    column.name.toLowerCase().includes('private notes') &&
    userStore.userId === column.privateUid
  )
}

/**
 * Determines if current user can edit a given column.
 *
 * Returns true if:
 * - User owns the private notes column
 * - User is full editor
 * - Guest was given access as partial editor of column
 */
export function canUserEditColumn(column: Column): boolean {
  // It's not really a hook
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { hasPermission } = useRundownAccess()

  // User can always edit their own private notes column
  if (getIsPrivateNotesColumn(column)) {
    return true
  }

  // Full editors can edit any column
  if (hasPermission(RundownPermission.EDIT)) {
    return true
  }

  // Partial editors can only edit columns in editableColumns list
  if (
    hasPermission(RundownPermission.EDIT_PARTIAL) &&
    gridState.editableColumns.includes(column.id)
  ) {
    return true
  }

  return false
}
